import React, { useState, useEffect } from 'react';
import api from '../api';
import { FileText, CurrencyDollar, Eye, FilePdf, CheckCircle, Clock, Image } from '@phosphor-icons/react';
import { useAuth } from '../AuthContext';
import AtendimentosPrestador from '../components/AtendimentosPrestador';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const PrestadorDashboard = () => {
  const { userProfile, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [prestador, setPrestador] = useState(null);
  const [activeTab, setActiveTab] = useState('atendimentos');
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const statsRes = await api.get('/dashboard/stats');
      setStats(statsRes.data);
      const prestadoresRes = await api.get('/prestadores');
      const prestadorData = prestadoresRes.data.find(p => p.email === userProfile?.email);
      setPrestador(prestadorData);
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen bg-[#050505] text-white">Carregando...</div>;

  return (
    <div className="min-h-screen bg-[#050505]">
      <header className="bg-[#050505] border-b border-[#27272A] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <img src="/toolserv-logo.png" alt="Toolserv" className="h-8" />
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-white font-medium">{userProfile?.name}</div>
              <div className="text-zinc-400 text-xs">Prestador</div>
            </div>
            <button onClick={logout} className="px-4 py-2 bg-[#1E1E1E] text-white border border-[#27272A] rounded-sm hover:bg-[#27272A] transition-colors text-sm" data-testid="logout-button">Sair</button>
          </div>
        </div>
      </header>

      <div className="bg-[#121212] border-b border-[#27272A]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-2">
            {[
              { id: 'atendimentos', label: 'Meus Atendimentos', icon: FileText },
              { id: 'pagamento', label: 'Financeiro', icon: CurrencyDollar }
            ].map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${activeTab === tab.id ? 'border-[#FFB800] text-[#FFB800]' : 'border-transparent text-zinc-400 hover:text-white'}`} data-testid={`tab-${tab.id}`}>
                <tab.icon size={20} />{tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-[#121212] border border-[#27272A] rounded-sm p-6">
            <div className="text-zinc-400 text-sm mb-2">Total de Atendimentos</div>
            <div className="metric-value">{stats?.total_reports || 0}</div>
          </div>
          <div className="bg-[#121212] border border-[#27272A] rounded-sm p-6">
            <div className="text-zinc-400 text-sm mb-2">Horas Trabalhadas</div>
            <div className="metric-value">{stats?.total_hours_worked?.toFixed(1) || 0}</div>
          </div>
        </div>

        {activeTab === 'atendimentos' && prestador && <AtendimentosPrestador prestadorId={prestador.id} />}
        {activeTab === 'pagamento' && prestador && <RelatorioPagamento prestadorId={prestador.id} prestador={prestador} />}
      </div>
    </div>
  );
};

const RelatorioPagamento = ({ prestadorId, prestador }) => {
  const [relatorio, setRelatorio] = useState(null);
  const [payments, setPayments] = useState([]);
  const [mes, setMes] = useState('');
  const [loading, setLoading] = useState(false);
  const [detailReport, setDetailReport] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [fotoExpandida, setFotoExpandida] = useState(null);

  useEffect(() => {
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    setMes(currentMonth);
    fetchData(currentMonth);
  }, []);

  const fetchData = async (month) => {
    setLoading(true);
    try {
      const [relRes, payRes] = await Promise.all([
        api.get(`/prestadores/${prestadorId}/relatorio-pagamento?mes=${month}`),
        api.get(`/pagamentos-prestadores?prestador_id=${prestadorId}`)
      ]);
      setRelatorio(relRes.data);
      setPayments(payRes.data);
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  const handleMesChange = (e) => {
    const newMes = e.target.value;
    setMes(newMes);
    fetchData(newMes);
  };

  const openDetail = async (solicitacaoId) => {
    try {
      const [solRes, relRes] = await Promise.all([
        api.get(`/solicitacoes/${solicitacaoId}`),
        api.get(`/solicitacoes/${solicitacaoId}/relatorio`).catch(() => ({ data: null }))
      ]);
      setDetailReport({ ...solRes.data, relatorio_data: relRes.data });
      setIsDetailOpen(true);
    } catch (error) { console.error('Error:', error); }
  };

  const generatePDF = async (atendimento) => {
    try {
      const { default: jsPDF } = await import('jspdf');
      const doc = new jsPDF();

      try {
        const logoImg = await loadImageAsBase64(window.location.origin + '/toolserv-logo.png');
        if (logoImg) { doc.setFillColor(5, 5, 5); doc.rect(0, 0, 210, 30, 'F'); doc.addImage(logoImg, 'PNG', 15, 5, 50, 20); }
      } catch { doc.setFillColor(5, 5, 5); doc.rect(0, 0, 210, 30, 'F'); doc.setFontSize(16); doc.setTextColor(255, 184, 0); doc.text('TOOLSERV', 15, 20); }

      let y = 38;
      doc.setFontSize(10); doc.setTextColor(255, 184, 0);
      doc.text('RELATORIO DE ATENDIMENTO', 15, y); y += 10;
      doc.setTextColor(0, 0, 0); doc.setFontSize(9);
      doc.setFont(undefined, 'bold');
      doc.text(`Solicitacao: ${atendimento.numero_solicitacao}`, 15, y); y += 6;
      doc.setFont(undefined, 'normal');
      doc.text(`Data: ${atendimento.data_conclusao ? new Date(atendimento.data_conclusao).toLocaleDateString('pt-BR') : '-'}`, 15, y); y += 5;
      doc.text(`Horas: ${(atendimento.horas_executadas || 0).toFixed(1)}h`, 15, y); y += 5;

      if (prestador) {
        doc.text(`Valor/Hora: R$ ${prestador.valor_hora?.toFixed(2)}`, 15, y); y += 5;
        const valorTotal = (atendimento.horas_executadas || 0) * (prestador.valor_hora || 0) + (prestador.taxa_deslocamento || 0);
        doc.text(`Taxa Deslocamento: R$ ${prestador.taxa_deslocamento?.toFixed(2)}`, 15, y); y += 5;
        doc.setFont(undefined, 'bold');
        doc.text(`TOTAL: R$ ${valorTotal.toFixed(2)}`, 15, y); y += 10;
      }

      // Get report details
      try {
        const relRes = await api.get(`/solicitacoes/${atendimento.id}/relatorio`);
        const rel = relRes.data;
        if (rel) {
          doc.setFont(undefined, 'normal');
          if (rel.eletricista_nome) { doc.text(`Eletricista: ${rel.eletricista_nome}`, 15, y); y += 5; }
          if (rel.auxiliar_nome) { doc.text(`Auxiliar: ${rel.auxiliar_nome}`, 15, y); y += 5; }
          if (rel.observacoes) {
            y += 3;
            doc.setFont(undefined, 'bold'); doc.text('Observacoes:', 15, y); y += 5;
            doc.setFont(undefined, 'normal');
            const lines = doc.splitTextToSize(rel.observacoes, 175);
            lines.forEach(l => { if (y > 275) { doc.addPage(); y = 20; } doc.text(l, 15, y); y += 4; });
          }
          if (rel.servicos_executados?.length > 0) {
            y += 3;
            doc.setFont(undefined, 'bold'); doc.text('Servicos Executados:', 15, y); y += 5;
            doc.setFont(undefined, 'normal');
            rel.servicos_executados.forEach(s => { if (y > 275) { doc.addPage(); y = 20; } doc.text(`  - ${s.servico || s.categoria || ''}`, 15, y); y += 4; });
          }
        }
      } catch {}

      doc.setFontSize(7); doc.setTextColor(150,150,150);
      doc.text(`Gerado em ${new Date().toLocaleString('pt-BR')}`, 15, 290);
      doc.save(`relatorio-${atendimento.numero_solicitacao}.pdf`);
    } catch (error) { console.error('Error:', error); alert('Erro ao gerar PDF'); }
  };

  const loadImageAsBase64 = (url) => new Promise((resolve) => {
    const img = new window.Image(); img.crossOrigin = 'anonymous';
    img.onload = () => { const c = document.createElement('canvas'); c.width = img.width; c.height = img.height; c.getContext('2d').drawImage(img, 0, 0); resolve(c.toDataURL('image/jpeg', 0.7)); };
    img.onerror = () => resolve(null); img.src = url;
  });

  const totalPago = payments.reduce((acc, p) => acc + (p.valor || 0), 0);

  if (loading) return <div className="text-center py-8 text-zinc-400">Carregando...</div>;
  if (!relatorio) return null;

  return (
    <div>
      <div className="mb-6">
        <label className="block text-sm font-medium text-zinc-400 mb-2">Selecionar Mes</label>
        <input type="month" value={mes} onChange={handleMesChange} className="px-4 py-2 bg-[#121212] border border-[#27272A] rounded-sm text-white" data-testid="mes-select" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-[#121212] border border-[#27272A] rounded-sm p-6">
          <div className="text-zinc-400 text-sm mb-2">Atendimentos</div>
          <div className="text-3xl font-bold text-[#FFB800]" style={{ fontFamily: 'Barlow Condensed' }}>{relatorio.total_atendimentos}</div>
        </div>
        <div className="bg-[#121212] border border-[#27272A] rounded-sm p-6">
          <div className="text-zinc-400 text-sm mb-2">Horas</div>
          <div className="text-3xl font-bold text-[#FFB800]" style={{ fontFamily: 'Barlow Condensed' }}>{relatorio.total_horas.toFixed(1)}h</div>
        </div>
        <div className="bg-[#121212] border border-[#27272A] rounded-sm p-6">
          <div className="text-zinc-400 text-sm mb-2">A Receber (periodo)</div>
          <div className="text-3xl font-bold text-[#22C55E]" style={{ fontFamily: 'Barlow Condensed' }}>R$ {relatorio.total_a_receber.toFixed(2)}</div>
        </div>
        <div className="bg-[#121212] border border-[#27272A] rounded-sm p-6">
          <div className="text-zinc-400 text-sm mb-2">Total Pago</div>
          <div className="text-3xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }}>R$ {totalPago.toFixed(2)}</div>
        </div>
      </div>

      {/* Detalhamento */}
      <div className="bg-[#121212] border border-[#27272A] rounded-sm p-6 mb-6">
        <h3 className="text-xl font-semibold text-white mb-4" style={{ fontFamily: 'Barlow Condensed' }}>Detalhamento</h3>
        <div className="space-y-3">
          <div className="flex justify-between items-center p-3 bg-[#050505] rounded-sm">
            <div><div className="text-zinc-400 text-sm">Valor por Hora</div><div className="text-white">R$ {relatorio.valor_hora.toFixed(2)} x {relatorio.total_horas.toFixed(1)}h</div></div>
            <div className="text-[#22C55E] font-bold">R$ {relatorio.valor_horas.toFixed(2)}</div>
          </div>
          <div className="flex justify-between items-center p-3 bg-[#050505] rounded-sm">
            <div><div className="text-zinc-400 text-sm">Taxa de Deslocamento</div><div className="text-white">R$ {relatorio.taxa_deslocamento.toFixed(2)} x {relatorio.total_atendimentos}</div></div>
            <div className="text-[#22C55E] font-bold">R$ {relatorio.valor_deslocamentos.toFixed(2)}</div>
          </div>
        </div>
      </div>

      {/* Atendimentos with Visualizar + PDF */}
      {relatorio.atendimentos.length > 0 && (
        <div className="bg-[#121212] border border-[#27272A] rounded-sm p-6 mb-6">
          <h3 className="text-xl font-semibold text-white mb-4" style={{ fontFamily: 'Barlow Condensed' }}>Atendimentos do Periodo</h3>
          <div className="space-y-2">
            {relatorio.atendimentos.map((atendimento, idx) => {
              const valorTotal = (atendimento.horas_executadas || 0) * relatorio.valor_hora + relatorio.taxa_deslocamento;
              return (
                <div key={idx} className="flex justify-between items-center p-3 bg-[#050505] rounded-sm" data-testid={`atendimento-row-${idx}`}>
                  <div>
                    <div className="text-white font-medium">{atendimento.numero_solicitacao}</div>
                    <div className="text-zinc-400 text-sm">{atendimento.data_conclusao ? new Date(atendimento.data_conclusao).toLocaleDateString('pt-BR') : new Date(atendimento.created_at).toLocaleDateString('pt-BR')}</div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="text-[#FFB800] font-bold">{(atendimento.horas_executadas || 0).toFixed(1)}h</div>
                      <div className="text-[#22C55E] text-xs font-semibold">R$ {valorTotal.toFixed(2)}</div>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" onClick={() => openDetail(atendimento.id)} className="bg-[#1E1E1E] hover:bg-[#27272A] text-white h-8 px-2" data-testid={`view-atendimento-${idx}`}>
                        <Eye size={14} />
                      </Button>
                      <Button size="sm" onClick={() => generatePDF(atendimento)} className="bg-[#1E1E1E] hover:bg-[#27272A] text-white h-8 px-2" data-testid={`pdf-atendimento-${idx}`}>
                        <FilePdf size={14} />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Pagamentos Recebidos */}
      <div className="bg-[#121212] border border-[#27272A] rounded-sm p-6">
        <h3 className="text-xl font-semibold text-white mb-4" style={{ fontFamily: 'Barlow Condensed' }}>Pagamentos Recebidos</h3>
        {payments.length === 0 ? (
          <div className="text-zinc-500 text-center py-4">Nenhum pagamento registrado</div>
        ) : (
          <div className="space-y-2">
            {payments.map(p => (
              <div key={p.id} className="flex items-center justify-between p-3 bg-[#050505] rounded-sm" data-testid={`payment-row-${p.id}`}>
                <div>
                  <div className="text-white font-medium">{p.descricao}</div>
                  <div className="text-zinc-500 text-xs">{new Date(p.data + 'T00:00:00').toLocaleDateString('pt-BR')}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[#22C55E] font-bold">R$ {p.valor?.toFixed(2)}</span>
                  {p.comprovante_url && (
                    <a href={p.comprovante_url} target="_blank" rel="noopener noreferrer" className="text-[#FFB800] hover:text-[#E6A600] text-xs flex items-center gap-1"><Eye size={14} />Ver</a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-white">Detalhes do Atendimento</DialogTitle></DialogHeader>
          {detailReport && (
            <div className="space-y-4">
              <div className="p-4 bg-[#050505] border border-[#27272A] rounded-sm">
                <div className="text-2xl font-bold text-[#FFB800]" style={{ fontFamily: 'Barlow Condensed' }}>{detailReport.numero_solicitacao}</div>
                <Badge className={detailReport.status === 'concluida' ? 'bg-emerald-600 mt-2' : 'bg-orange-500 mt-2'}>{detailReport.status}</Badge>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><div className="text-zinc-500 text-xs">Horas Executadas</div><div className="text-[#FFB800] font-bold text-xl">{(detailReport.horas_executadas || 0).toFixed(1)}h</div></div>
                <div><div className="text-zinc-500 text-xs">Data</div><div className="text-white text-sm">{detailReport.data_conclusao ? new Date(detailReport.data_conclusao).toLocaleDateString('pt-BR') : '-'}</div></div>
              </div>
              <div><div className="text-zinc-500 text-xs mb-2">Servicos Solicitados</div>
                <div className="flex flex-wrap gap-2">{detailReport.servicos_solicitados?.map((s, i) => <span key={i} className="text-xs bg-[#050505] px-2 py-1 rounded text-zinc-300 border border-[#27272A]">{s}</span>)}</div>
              </div>
              {detailReport.relatorio_data && (
                <div className="border border-[#27272A] rounded-sm p-4 space-y-3">
                  <h4 className="text-[#FFB800] font-semibold text-sm">Relatorio</h4>
                  {detailReport.relatorio_data.eletricista_nome && <div><span className="text-zinc-500 text-xs">Eletricista: </span><span className="text-white text-sm">{detailReport.relatorio_data.eletricista_nome}</span></div>}
                  {detailReport.relatorio_data.auxiliar_nome && <div><span className="text-zinc-500 text-xs">Auxiliar: </span><span className="text-white text-sm">{detailReport.relatorio_data.auxiliar_nome}</span></div>}
                  {detailReport.relatorio_data.observacoes && <div><div className="text-zinc-500 text-xs mb-1">Observacoes</div><div className="text-white text-sm bg-[#050505] p-2 rounded-sm">{detailReport.relatorio_data.observacoes}</div></div>}
                  {detailReport.relatorio_data.servicos_executados?.length > 0 && (
                    <div><div className="text-zinc-500 text-xs mb-1">Servicos Executados</div>
                      <div className="flex flex-wrap gap-1">{detailReport.relatorio_data.servicos_executados.map((s, i) => <span key={i} className="text-xs bg-[#050505] px-2 py-1 rounded text-emerald-400 border border-emerald-800">{s.servico || s.categoria || ''}</span>)}</div>
                    </div>
                  )}
                  {detailReport.relatorio_data.fotos_depois?.length > 0 && (
                    <div><div className="text-zinc-500 text-xs mb-2 flex items-center gap-1"><Image size={14} />Fotos ({detailReport.relatorio_data.fotos_depois.length})</div>
                      <div className="grid grid-cols-3 gap-2">{detailReport.relatorio_data.fotos_depois.map((url, i) => <img key={i} src={url} alt={`Foto ${i+1}`} className="w-full h-24 object-cover rounded-sm border border-[#27272A] cursor-pointer hover:border-[#FFB800]" onClick={() => setFotoExpandida(url)} />)}</div>
                    </div>
                  )}
                  {detailReport.relatorio_data.status_aprovacao && (
                    <div className="pt-2 border-t border-[#27272A]">
                      <span className="text-zinc-500 text-xs">Aprovacao: </span>
                      <Badge className={detailReport.relatorio_data.status_aprovacao === 'aprovado' ? 'bg-emerald-600' : detailReport.relatorio_data.status_aprovacao === 'rejeitado' ? 'bg-red-500' : 'bg-yellow-500 text-black'}>{detailReport.relatorio_data.status_aprovacao}</Badge>
                      {detailReport.relatorio_data.aprovado_por && <span className="text-zinc-400 text-xs ml-2">por {detailReport.relatorio_data.aprovado_por}</span>}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Foto Expandida */}
      <Dialog open={!!fotoExpandida} onOpenChange={() => setFotoExpandida(null)}>
        <DialogContent className="bg-[#121212] border-[#27272A] max-w-3xl p-2">
          {fotoExpandida && <img src={fotoExpandida} alt="Foto" className="w-full max-h-[80vh] object-contain rounded-sm" />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PrestadorDashboard;
